import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { Dialog } from 'primereact/dialog';

import { useToastNotification } from '@/hooks';
import { Button, InputTextArea, LoadingSpinner } from '@/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';

import { editCommentsAccountPayment } from '@/services/editCommentsAccountStatement';
import { DocumentoSeleccionado } from '@/types/getPendingPaymentsType';

type EditAccountStamentModalProps = {
  visible: boolean;
  onClose: () => void;
  paymentsOids: DocumentoSeleccionado[];
  getData: () => void;
};

const EditAccountStamentModal: FC<EditAccountStamentModalProps> = ({
  visible,
  onClose,
  paymentsOids,
  getData,
}) => {
  const { t } = useTranslation('accountStatement');
  const { showToast } = useToastNotification();
  const [comments, setComments] = useState<string>('');
  const errorsComments = '';

  const editPaymentsSelected = useCallback(async () => {
    const promises = paymentsOids.map((item) =>
      editCommentsAccountPayment({
        documentoOid: [String(item.oidDocumento)],
        comentario: comments,
      })
    );

    return Promise.allSettled(promises);
  }, [comments, paymentsOids]);

  const { mutate: editPayments, isPending: isLoading } = useMutation({
    mutationFn: editPaymentsSelected,
    onSuccess: (res): void => {
      const count = res.filter(({ status }) => status === 'fulfilled').length;
      const allDeleted = count === paymentsOids.length;

      if (count) {
        showToast({
          severity: 'success',
          detail:
            count >= 2
              ? t('accountsUpdatedPaymentsSuccess', { count })
              : t('accountUpdatedPaymentsSuccess', { count }),
        });
        setComments('');
      }

      if (allDeleted) {
        onClose();
        getData();
        return;
      }

      const errors = res
        .filter(
          (item): item is PromiseRejectedResult => item?.status === 'rejected'
        )
        .map(({ reason }) => reason);

      errors.forEach((error) =>
        showToast({
          severity: 'error',
          detail: error?.message,
        })
      );
    },
    onError: (error) =>
      showToast({
        severity: 'error',
        detail: error?.message,
      }),
  });

  const toggleDialog = () => {
    onClose();
  };

  const dialogHeader = (
    <div style={{ color: '#223466' }}>
      <FontAwesomeIcon
        icon={faPencil}
        style={{ marginRight: '0.5rem', color: '#223466' }}
      />
      {t('editComments')}
    </div>
  );

  const onEditButtonClick = () => {
    editPayments();
  };

  const footerContent = (
    <div className="w-full flex lg:justify-content-end justify-content-between">
      <Button
        label={t('cancel')}
        link
        onClick={toggleDialog}
        disabled={isLoading}
        style={{ color: '#170a33', width: '180px' }}
        className="flex text-center h-16"
      />
      <Button
        style={{
          backgroundColor: '#619b77',
          color: '#FFFFFF',
          width: '180px',
        }}
        className="flex flex-direction-row align-items-center justify-content-center h-16"
        label={t('saveChangesButton')}
        onClick={onEditButtonClick}
        loading={isLoading}
        icon="pi pi-save"
      />
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{ position: 'relative', width: '90%', maxWidth: '500px' }}
      onHide={toggleDialog}
      footer={footerContent}
    >
      {isLoading && (
        <div
          style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
          className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="lg:px-3 lg:pb-6 px-2 py-2">
        <p>{t('editCommentsSelected')}</p>
        <div className="flex flex-row my-2 items-">
          <div className="w-3/12 lg:flex hidden justify-center items-center mr-4">
            <div className="h-12 w-0.5 bg-gray-700" style={{ width: '2px' }} />
          </div>
          <div className="lg:w-9/12 w-full">
            <InputTextArea
              value={comments ?? ''}
              label={t('table.comments')}
              id="comments"
              name="comments"
              placeholder={t('table.comments')}
              onChange={(e) => setComments(e.target.value)}
              error={errorsComments}
              inputClassName="w-full"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default EditAccountStamentModal;
